import { useState, useEffect, useMemo } from 'react';
import { SquareArrowOutUpRight } from 'lucide-react';
import { VStack } from '@components/base/layout';
import { Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Alert, Spinner } from '@components/base/feedback';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  usePublishLayoutConfig,
  withComponentRules,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import {
  documentsService,
  type VeosDocumentRow,
  type VeosDocumentContent,
  type VeosClassementOption,
} from '../../api/documentsService';
import { getDocumentKind, openBase64InNewTab, toDataUri } from './documentHelpers';
import { useBlobUrl } from './useBlobUrl';
import { buildClassementConfig, useDocumentClassement } from './documentClassement';
import {
  DocumentClassementInputs,
  DocumentClassementStatus,
} from './DocumentClassementForm';

interface DocumentPreviewProps {
  row: VeosDocumentRow;
  classementOptions: VeosClassementOption[];
  /**
   * Called after a successful classement/libellé save so the host can refresh
   * its document list. Optional to mirror the other claimflow cards' page-wired
   * runtime callbacks (`OpenCoverageCard.onCoverageOpened`), which lets the
   * registration `propsSchema` omit it cleanly — `DocumentsCard` always passes
   * it at the real render site.
   */
  onSaved?: () => void;
  /**
   * Per-tenant layout overrides injected by the `withComponentRules` HOC.
   * Underscored to match the injected meta-prop convention; `undefined` at
   * baseline ⇒ the config is returned unchanged (byte-identical render).
   *
   * Carries the `fields` bucket keyed by the two stable autosave-input keys
   * (`classement`, `libelle`); merged into the config that drives the two
   * bespoke inputs' label / placeholder / visibility / order. The card always
   * owns the autosave-on-change/blur behaviour and the `updateGed` round-trip —
   * overrides only mutate the declarative presentation, never the persist path.
   */
  _layoutOverrides?: LayoutOverrides;
}

const PREVIEW_ERROR = "Erreur lors du chargement de l'aperçu.";

export function DocumentPreview({
  row,
  classementOptions,
  onSaved,
  _layoutOverrides,
}: Readonly<DocumentPreviewProps>) {
  const [content, setContent] = useState<VeosDocumentContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Classement + Libellé autosave form (shared with the email view). `TYPE`
  // is 'DOC' — this component only ever renders non-email rows (API='DOC').
  const { cdDs, libelle, isSaving, saveError, setLibelle, onSelectChange, onLibelleBlur } =
    useDocumentClassement(row, 'DOC', onSaved);

  useEffect(() => {
    let cancelled = false;

    documentsService
      .fetchDocumentContent(row.ID_ADOC)
      .then((data) => {
        if (cancelled) return;
        setContent(data);
        setLoadError(null);
      })
      .catch(() => {
        if (!cancelled) setLoadError(PREVIEW_ERROR);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [row.ID_ADOC]);

  const contentType = content?.contentType ?? row.CONTENT_TYPE;

  // Declarative field config, merged with any per-tenant layout overrides.
  // Baseline (no overrides) ⇒ `applyLayoutOverrides` returns the base config
  // unchanged (referential identity), so labels/placeholders/order/visibility
  // are byte-identical to the pre-migration inline JSX.
  const baseConfig = useMemo(() => buildClassementConfig('claimflow.documentPreview.form'), []);
  const formConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  // Publish the base config so the admin layout editor surfaces both inputs as
  // rename / hide / reorder / placeholder targets.
  usePublishLayoutConfig(
    'claimflow.documentPreview',
    buildPublishedConfigFromEntityForm(baseConfig, 'Document Preview'),
  );

  const kind = content
    ? getDocumentKind({ ...row, CONTENT_TYPE: content.contentType ?? row.CONTENT_TYPE })
    : getDocumentKind(row);

  // A PDF is *framed*, and the deployed CSP (`frame-src 'self' blob:`) refuses
  // a `data:` frame — so the iframe is fed a same-origin object URL instead.
  // Only minted for PDFs; the `<img>` branch keeps its data URI (`img-src`
  // allows `data:`). See `docs/SECURITY-HEADERS.md`.
  const { url: pdfUrl, failed: pdfFailed } = useBlobUrl(
    kind === 'pdf' ? content?.datas : undefined,
    'application/pdf',
  );

  // An undecodable payload surfaces the same inline error as a failed fetch.
  const previewError = loadError ?? (pdfFailed ? PREVIEW_ERROR : null);

  return (
    <VStack gap={4}>
      {/* Classement + Libellé (above the preview, side by side). Both inputs
          render from the declarative, override-merged config. */}
      <DocumentClassementInputs
        formConfig={formConfig}
        classementOptions={classementOptions}
        cdDs={cdDs}
        libelle={libelle}
        onSelectChange={onSelectChange}
        onLibelleChange={setLibelle}
        onLibelleBlur={onLibelleBlur}
      />

      <DocumentClassementStatus isSaving={isSaving} saveError={saveError} />

      {/* Dark letterboxed preview frame. */}
      <div className="bg-secondary-900 rounded-lg overflow-hidden w-full aspect-[210/297] flex items-center justify-center p-3">
        {isLoading && <Spinner size="md" />}
        {!isLoading && previewError && (
          <div className="p-4 w-full">
            <Alert variant="error">{previewError}</Alert>
          </div>
        )}
        {!isLoading && !previewError && content && kind === 'pdf' && pdfUrl && (
          <iframe
            title={row.LIB_ADOC}
            src={pdfUrl}
            className="w-full h-full border-0 bg-white rounded"
          />
        )}
        {!isLoading && !previewError && content && kind === 'image' && (
          <img
            alt={row.LIB_ADOC}
            src={toDataUri(content.datas, contentType)}
            className="max-w-full max-h-full object-contain"
          />
        )}
        {!isLoading && !previewError && content && kind !== 'pdf' && kind !== 'image' && (
          <VStack gap={3} align="center" className="p-6">
            <Text size="sm" className="text-white">
              Aperçu indisponible pour ce type de fichier.
            </Text>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<SquareArrowOutUpRight className="w-4 h-4" />}
              onClick={() => {
                // Non-displayable types download — under the real file name.
                openBase64InNewTab(
                  content.datas,
                  contentType,
                  content.name ?? row.NOM_ADOC ?? undefined,
                );
              }}
            >
              Ouvrir le fichier
            </Button>
          </VStack>
        )}
      </div>
    </VStack>
  );
}

/**
 * Rule-aware wrapped `DocumentPreview`. `DocumentsCard` renders this constant
 * (instead of the raw `DocumentPreview`) so admin-authored rules fire on every
 * render — in particular `renameField` / `hideField` / `reorderFields` /
 * `setFieldPlaceholder` on the `classement` / `libelle` field keys retext,
 * hide, or reorder the two autosave inputs without a code deploy.
 *
 * Co-located here (not in `./register`) deliberately: `register.ts` imports
 * `DocumentsCard`, so wrapping here and importing this from
 * `./documents/DocumentPreview` avoids a register↔DocumentsCard import cycle.
 * The registration (id + raw component + schema) still lives in `register.ts`.
 *
 * The `react-refresh/only-export-components` disable below is intentional: the
 * wrapped component is co-located with the raw `DocumentPreview` on purpose
 * (to avoid a register↔DocumentsCard import cycle), and both exports are
 * components, so the only "cost" is fast-refresh granularity in dev.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const DocumentPreviewConfigurable = withComponentRules(
  DocumentPreview,
  'claimflow.documentPreview',
);
